import React, { createContext, useContext, useMemo } from 'react'
import type { CSSProperties, PropsWithChildren } from 'react'
import type { DraggableSyntheticListeners, UniqueIdentifier } from '@dnd-kit/core'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Text, Box, IconDragDots, TooltipRenderer } from '@machobear/design-system'

import { useTranslation } from 'react-i18next'
import { sortableItem, dragHandle } from './SortableItem.css'

interface Props {
  id: UniqueIdentifier
}

interface Context {
  attributes: Record<string, any>
  listeners: DraggableSyntheticListeners
  ref(node: HTMLElement | null): void
}

const SortableItemContext = createContext<Context>({
  attributes: {},
  listeners: undefined,
  ref() {}
})

export function SortableItem({ children, id }: PropsWithChildren<Props>) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition
  } = useSortable({ id })
  const context = useMemo(
    () => ({
      attributes,
      listeners,
      ref: setActivatorNodeRef
    }),
    [attributes, listeners, setActivatorNodeRef]
  )

  const style: CSSProperties = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Translate.toString(transform),
    transition
  }

  return (
    <SortableItemContext.Provider value={context}>
      <li className={sortableItem} ref={setNodeRef} style={style}>
        {children}
      </li>
    </SortableItemContext.Provider>
  )
}

export function DragHandle({ id }: { id: string }) {
  const { t } = useTranslation()
  const { attributes, listeners, ref } = useContext(SortableItemContext)

  return (
    <button className={dragHandle} {...attributes} {...listeners} ref={ref}>
      <TooltipRenderer
        id={id}
        tooltip={
          <Box paddingY="xxxsmall" justifyContent="center">
            <Text weight="strong" size="small">
              {t('Drag to reorder photos')}
            </Text>
          </Box>
        }
      >
        {({ triggerProps }) => (
          <Box {...triggerProps}>
            <IconDragDots size="small" />
          </Box>
        )}
      </TooltipRenderer>
    </button>
  )
}
